import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { PureButton } from '../../basics';
import { Button, ChevronIcon, Link } from '../../components';
import { Badge } from '../../components/Badge';
import { DropdownAlignment, InputSizes, LinkVariant, RenderType } from '../../enums';
import { useClickOutside } from '../../hooks';
import { commonStyles, dropdownStyles } from '../scss';
import styles from './FilterDropdownBase.module.scss';
const FilterDropdownBase = ({ a11yButtonText = '', a11yIndicatorText, a11ySubmitText = '', alignment = DropdownAlignment.Left, children, className, dataTestId, filterDropdownButtonClasses, filterDropdownOverlayClasses, hasChanged, hasFormFooter = true, label, numSelected, onClickOutside, onOpen, onReset, onSubmit, resetLabel, showNumSelected, size = InputSizes.Standard, submitLabel, title, }) => {
    const mainRef = useRef(null);
    const buttonRef = useRef(null);
    const contentRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [activeAlignment, setActiveAlignment] = useState(alignment === DropdownAlignment.Auto ? DropdownAlignment.Left : alignment);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [parentWidth, setParentWidth] = useState(0);
    const showIndicator = showNumSelected && !!numSelected;
    const disableReset = !hasChanged && !numSelected;
    const submit = () => {
        onSubmit?.();
        setIsOpen(false);
    };
    const reset = () => {
        onReset?.();
        setIsOpen(false);
    };
    const handleClickOutside = () => {
        if (isOpen) {
            onClickOutside();
            setIsOpen(false);
        }
    };
    const handleDropdownBaseClick = () => {
        if (isOpen) {
            handleClickOutside();
        }
        else {
            if (onOpen) {
                onOpen();
            }
            setIsOpen(true);
        }
    };
    useClickOutside(mainRef, handleClickOutside);
    useEffect(() => {
        const maxContentWidth = 320;
        if (alignment === DropdownAlignment.Auto &&
            currentOffset &&
            parentWidth &&
            maxContentWidth &&
            currentOffset + maxContentWidth > parentWidth) {
            setActiveAlignment(DropdownAlignment.Right);
            return;
        }
        if (alignment === DropdownAlignment.Auto) {
            setActiveAlignment(DropdownAlignment.Left);
        }
    }, [alignment, currentOffset, isOpen, parentWidth]);
    useEffect(() => {
        setCurrentOffset(mainRef?.current?.offsetLeft || 0);
        setParentWidth(mainRef?.current?.parentElement?.offsetWidth || 0);
    }, [currentOffset, isOpen, numSelected, parentWidth]);
    useEffect(() => {
        setActiveAlignment(alignment);
    }, [alignment]);
    return (_jsxs("div", { ref: mainRef, className: classNames(className, 'position-relative'), title: title, children: [_jsxs(PureButton, { ref: buttonRef, className: classNames('d-flex align-items-center', dropdownStyles.dropdownPureButton, styles.filterDropdownBaseButton, {
                    [styles.showIndicator]: showIndicator,
                    [styles.isOpen]: isOpen,
                    [dropdownStyles.isSmall]: size === InputSizes.Small,
                }, filterDropdownButtonClasses), onClick: handleDropdownBaseClick, "data-testid": dataTestId, "aria-label": a11yButtonText, children: [_jsx("div", { className: "text-nowrap text-truncate", children: label }), showIndicator && (_jsx(Badge, { className: "ml-1x", "aria-label": a11yIndicatorText, children: numSelected })), _jsx(ChevronIcon, { className: classNames(dropdownStyles.chevronIcon, {
                            [dropdownStyles.isOpen]: isOpen,
                        }), size: 24 })] }), isOpen && (_jsxs("div", { ref: contentRef, className: classNames(dropdownStyles.dropdownBaseContainer, styles.filterDropdownContainer, {
                    [styles.left]: activeAlignment === DropdownAlignment.Left,
                    [styles.right]: activeAlignment === DropdownAlignment.Right,
                }, filterDropdownOverlayClasses), children: [_jsx("div", { className: styles.filterDropdownContent, children: children }), hasFormFooter && (_jsxs("div", { className: "d-flex justify-content-between mt-3x px-2x", children: [_jsx(Link, { className: classNames('d-flex align-items-center', {
                                    [commonStyles.isDisabled]: disableReset,
                                }), as: RenderType.Button, variant: LinkVariant.Brand, onClick: reset, disabled: disableReset, dataTestId: "reset-button", children: resetLabel }), _jsx(Button, { onClick: submit, className: styles.pillButton, disabled: !hasChanged, dataTestId: "submit-button", a11yText: a11ySubmitText, children: submitLabel })] }))] }))] }));
};
export { FilterDropdownBase };
